import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./SortByButton.module.scss";
import { dataItem } from "@/widgets/categoriesRevenueModal/libs/data/dataItem";
import { filterAppsDataActions } from "@/entities/filterAppsData";
import { filterAppsActions, getFilterAppsState } from "@/entities/filterApps";
import { ChevronDownIcon } from "lucide-react";
import { useMe, useCurrentOrganization } from "@/Hooks";
import mixpanel from "@/shared/lib/mixpanel";

export const SortByButton = ({ setSortMenuOpen }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const { filterSort } = useSelector(getFilterAppsState);

  const [selectedItem, setSelectedItem] = useState(filterSort);
  const sortDirection = useSelector((state) => state.filterApps.sortDirection);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { me } = useMe(); // Get user info, if available
  const currentOrganization = useCurrentOrganization(); // Get org info, if available
  const previousSortFieldRef = useRef(filterSort);
  const previousSortDirectionRef = useRef(sortDirection);

  useEffect(() => {
    setSortMenuOpen(isOpen);
  }, [isOpen]);

  const handleClickItem = (
    index: number,
    text: string,
    filter: string,
    event?: React.MouseEvent,
  ) => {
    event?.stopPropagation();

    if (filterSort !== filter) {
      mixpanel.track("Sort Order Changed", {
        sort_by_field: filter,
        sort_direction: sortDirection, // Direction remains the same initially when changing field
        sort_by_label: text,
        sort_direction_label:
          sortDirection === "asc"
            ? getSortingAscLabel(filter)
            : getSortingDescLabel(filter),
        previous_sort_by_field: previousSortFieldRef.current,
        previous_sort_direction: previousSortDirectionRef.current,
        UserEmail: me?.email || "",
        OrganizationID: currentOrganization?.uuid || "",
        trigger: "field_change",
      });
    }

    // Set the selected item in local state
    setSelectedItem(filter);

    // Reset pagination and data BEFORE applying new filter to ensure only page 1 is fetched
    dispatch(filterAppsActions.resetPageFilter());
    dispatch(filterAppsActions.resetPageFilterSidebar());
    dispatch(filterAppsDataActions.resetFilterAppDataSortedAll());
    dispatch(filterAppsDataActions.resetFilterAppDataSorted());
    dispatch(filterAppsDataActions.resetFilterAppData());

    // Apply the new filter
    dispatch(filterAppsActions.setFilterSort(filter));

    // Ensure sort direction is applied
    dispatch(filterAppsActions.setSortDirection(sortDirection));

    // Update previous refs for next tracking
    previousSortFieldRef.current = filter;
  };

  const handleSortDirectionChange = (
    direction: "asc" | "desc",
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (sortDirection === direction) return;

    const currentSortOption = dataItem.find(
      (item) => item.filter === selectedItem,
    );
    const sortFieldLabel = currentSortOption
      ? currentSortOption.text
      : selectedItem;

    mixpanel.track("Sort Order Changed", {
      sort_by_field: selectedItem,
      sort_direction: direction,
      sort_by_label: sortFieldLabel,
      sort_direction_label:
        direction === "asc"
          ? getSortingAscLabel(selectedItem)
          : getSortingDescLabel(selectedItem),
      previous_sort_by_field: previousSortFieldRef.current,
      previous_sort_direction: previousSortDirectionRef.current,
      UserEmail: me?.email || "",
      OrganizationID: currentOrganization?.uuid || "",
      trigger: "direction_change",
    });

    // Reset page counter first to prevent pagination increment
    dispatch(filterAppsActions.resetPageFilter());
    dispatch(filterAppsActions.resetPageFilterSidebar());

    // Then reset data to trigger fresh fetch with new sort direction
    dispatch(filterAppsDataActions.resetFilterAppDataSortedAll());
    dispatch(filterAppsDataActions.resetFilterAppDataSorted());
    dispatch(filterAppsDataActions.resetFilterAppData());

    // Finally set the new sort direction
    dispatch(filterAppsActions.setSortDirection(direction));

    // Update the previous refs for next tracking
    previousSortDirectionRef.current = direction;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setSelectedItem(filterSort);
  }, [filterSort]);

  const getSortingDescLabel = (
    filterSort: string,
    event?: React.MouseEvent,
  ) => {
    event?.stopPropagation();
    switch (filterSort) {
      case "created_at":
        return "Newest";
      case "name":
        return "Z - A";
      case "download":
        return "Higher";
      case "rev_install_rate":
        return "Higher";
      case "release_date":
        return "Newest";
      case "revenue":
        return "Highest";
      case "onboarding_step_count":
        return "Highest";
      default:
        return "Descending";
    }
  };

  const getSortingAscLabel = (filterSort: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    switch (filterSort) {
      case "created_at":
        return "Oldest";
      case "name":
        return "A - Z";
      case "download":
        return "Lower";
      case "rev_install_rate":
        return "Lower";
      case "release_date":
        return "Oldest";
      case "revenue":
        return "Lowest";
      case "onboarding_step_count":
        return "Lowest";
      default:
        return "Ascending";
    }
  };

  const getSelectedSortDisplayText = () => {
    const currentSortOption = dataItem.find(
      (item) => item.filter === selectedItem,
    );
    return currentSortOption ? currentSortOption.text : "Sort"; // Fallback to "Sort" if not found
  };

  return (
    <>
      {isOpen && (
        <div className={styles.overlay} onClick={() => setIsOpen(false)} />
      )}
      <div
        ref={dropdownRef}
        className={styles.sortBy}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <img src="/img/blackIcon/Sort.svg" alt="Sort By" />
          <p className={styles.sortByText}>{getSelectedSortDisplayText()}</p>
          <ChevronDownIcon width={16} height={16} />
        </div>

        {isOpen && (
          <div className={styles.sortByMenu}>
            {dataItem.map((item, index) => (
              <div
                key={item.text}
                className={`${styles.sortByMenuItem} ${selectedItem === item.filter ? styles.active : ""}`}
                onClick={(e) =>
                  handleClickItem(index, item.text, item.filter, e)
                }
              >
                {item.text}
              </div>
            ))}
            <div className={styles.sortByDescendingAscending}>
              {selectedItem === "name" ? (
                <>
                  <button
                    className={`${styles.sortByMenuItem} ${sortDirection === "asc" ? styles.activeDescendingAscending : ""}`}
                    onClick={(e) => handleSortDirectionChange("asc", e)}
                  >
                    {getSortingAscLabel(selectedItem)}
                  </button>
                  <button
                    className={`${styles.sortByMenuItem} ${sortDirection === "desc" ? styles.activeDescendingAscending : ""}`}
                    onClick={(e) => handleSortDirectionChange("desc", e)}
                  >
                    {getSortingDescLabel(selectedItem)}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={`${styles.sortByMenuItem} ${sortDirection === "desc" ? styles.activeDescendingAscending : ""}`}
                    onClick={(e) => handleSortDirectionChange("desc", e)}
                  >
                    {getSortingDescLabel(selectedItem)}
                  </button>
                  <button
                    className={`${styles.sortByMenuItem} ${sortDirection === "asc" ? styles.activeDescendingAscending : ""}`}
                    onClick={(e) => handleSortDirectionChange("asc", e)}
                  >
                    {getSortingAscLabel(selectedItem)}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
